import React from "react"
import { useTranslation } from "react-i18next"

import { BillPdfPreviewAnalysisPanel } from "@/modules/data/components/bill-pdf-preview-analysis-panel"
import { BillPdfPreviewGrid } from "@/modules/data/components/bill-pdf-preview-grid"
import type {
  BillPdfPreviewRowEdit,
  BillPdfUploadFileStatus,
  BillPdfUploadSession,
} from "@/modules/data/services/use-bill-pdf-upload-session"
import { isUnfinishedBillPdfFileState } from "@/modules/data/utils/bill-pdf-preview-state"

import { Button, Stack, Tooltip, Typography } from "@mui/material"

interface BillPdfReviewSectionProps {
  cancelIsPending: boolean
  confirmIsPending: boolean
  onCancel: () => void
  onConfirm: () => void
  onRemove?: (rowId: string) => void
  onUpdate?: (rowId: string, edit: BillPdfPreviewRowEdit) => Promise<void>
  refreshFiles?: () => Promise<BillPdfUploadFileStatus[]>
  removeIsPending?: boolean
  session: BillPdfUploadSession
}

export const BillPdfReviewSection: React.FC<BillPdfReviewSectionProps> = ({
  cancelIsPending,
  confirmIsPending,
  onCancel,
  onConfirm,
  onRemove,
  onUpdate,
  refreshFiles,
  removeIsPending = false,
  session,
}) => {
  const { t } = useTranslation(["data", "common"])
  const hasUnfinishedFile = session.files.some((file) =>
    isUnfinishedBillPdfFileState(file.state)
  )
  const isParsing = session.status === "parsing" || hasUnfinishedFile
  const isImporting = session.status === "importing"
  const zeroReady =
    !isParsing && !session.rows.some((row) => row.status === "ready")
  const completeDisabled =
    isParsing || isImporting || zeroReady || cancelIsPending || removeIsPending
  const showNothingToImportHint = zeroReady && !isImporting
  // The server only allows changing preview rows once every file has parsed
  const canChangeRows =
    !isParsing && !isImporting && !confirmIsPending && !cancelIsPending

  const completeButton = (
    <Button
      disabled={completeDisabled}
      loading={confirmIsPending}
      onClick={onConfirm}
      variant="contained"
    >
      {t("upload.billPdf.completeUpload")}
    </Button>
  )

  return (
    <Stack data-testid="bill-pdf-review-section" sx={{ gap: 2, pt: 1 }}>
      {isParsing ? null : (
        <>
          <Typography variant="h3">
            {t("upload.billPdf.parsedBillsTitle")}
          </Typography>
          <BillPdfPreviewAnalysisPanel sessionId={session.id} />
        </>
      )}
      <BillPdfPreviewGrid
        files={session.files}
        onRemove={canChangeRows ? onRemove : undefined}
        onUpdate={canChangeRows ? onUpdate : undefined}
        refreshFiles={refreshFiles}
        rows={session.rows}
      />
      <Stack sx={{ flexDirection: "row", gap: 1 }}>
        <Button
          disabled={cancelIsPending || confirmIsPending}
          onClick={onCancel}
          variant="outlined"
        >
          {t("button.cancel", { ns: "common" })}
        </Button>
        {showNothingToImportHint ? (
          <Tooltip title={t("upload.billPdf.nothingToImport")}>
            <span>{completeButton}</span>
          </Tooltip>
        ) : (
          completeButton
        )}
      </Stack>
    </Stack>
  )
}
